import { StarIcon } from "@heroicons/react/24/solid";
import { EventFragment } from "../../__generated__/graphql";
import useNav from "../../hooks/useNavigation";
import ParticipantsAvatars from "../ParticipantsAvatars";
import Checkbox from "../Checkbox";
import { cn } from "../../lib/utils";
import DraftBadge from "../DraftBadge";

interface EventPreviewProps {
  event: EventFragment;
  selectable?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (eventId: string) => void;
  onNavigateToDetails?: (event: EventFragment) => void;
}

export default function EventPreview({
  event,
  selectable = false,
  isSelected = false,
  onToggleSelect,
  onNavigateToDetails,
}: EventPreviewProps) {
  const navigateWithLog = useNav();

  const imageUrl = event.eventLocation?.location.photoUrls?.[0] || undefined;

  const handleCardClick = () => {
    if (!selectable) {
      navigateWithLog(`/events/${event.id}`);
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleSelect) {
      onToggleSelect(event.id);
    }
  };

  const handleNavigateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Use callback if provided (for in-page navigation via nuqs)
    if (onNavigateToDetails) {
      onNavigateToDetails(event);
    } else if (event.projectId) {
      // Navigate to the project's events tab with this event selected
      navigateWithLog(
        `/projects/${event.projectId}?tab=events&eventId=${event.id}`
      );
    } else {
      navigateWithLog(`/events/${event.id}`);
    }
  };

  return (
    <div
      className={cn(
        "border rounded-md relative transition-all duration-300",
        selectable
          ? "border-border"
          : "border-border cursor-pointer hover:opacity-80",
        isSelected && " border-accent rounded-md"
      )}
      onClick={handleCardClick}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="Event image"
          className="w-full h-30 object-cover rounded-md"
        />
      ) : (
        <div className="w-full h-30 bg-subCard rounded-md" />
      )}
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50 transition-colors rounded-md" />

      <div className="absolute bottom-0 left-0 right-0 p-4 flex flex-col justify-between h-full">
        <div className="flex flex-row justify-between items-center">
          <div className="flex gap-2 items-center">
            {selectable ? (
              <div
                className="cursor-pointer p-1 hover:bg-black/20 rounded transition-colors"
                onClick={handleCheckboxClick}
                title="Filtrer par cet évènement"
              >
                <Checkbox checked={isSelected} />
              </div>
            ) : (
              <>
                <StarIcon className="w-4 h-4 text-accent" />
                <div className="text-xs uppercase text-accent">Évènement</div>
              </>
            )}
            {event.isDraft && <DraftBadge className="bg-yellow-500/30" />}
          </div>

          <ParticipantsAvatars
            withBackground
            participants={
              event.eventParticipants
                ?.filter((participant) => participant !== null)
                .map((participant) => ({
                  id: participant?.id,
                  publicUserOrganization: participant?.publicUserOrganization,
                })) ?? []
            }
          />
        </div>
        <div className="flex flex-col">
          <div className="text-primary font-bold text-xl truncate">
            {event.name}
          </div>
          <div className="text-sm text-primary truncate">
            {event.eventLocation?.location?.label}
          </div>
          {selectable && (
            <button
              onClick={handleNavigateClick}
              className="text-xs text-accent hover:text-accent/80 text-left w-fit transition-colors cursor-pointer"
              title="Voir les détails"
            >
              Voir les détails →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
